import toastManager from "../toastManager";

import {
  sanitizePairConfiguration,
  sanitizeImageDetails,
  sanitizeTags,
  appendImagesToFormDataCustomField,
  getValidPairConfiguration,
  sanitizeEntryDataResponseBase,
} from "./commonUtils";

/**
 * Processes the initial response data from loading a segmentation entry.
 * Ensures all necessary fields have valid values and applies further sanitization on selected fields.
 *
 * @param {Object} param0 - An object.
 * @param {Object} param0.entryDataResponse - The initial response data from loading a segmentation entry.
 *
 * @returns {Object} The sanitized response, with all necessary fields having valid values.
 *
 * @example
 *
 * sanitizeSegmentationEntryDataResponse({ entryDataResponse: data });
 */
export const sanitizeSegmentationEntryDataResponse = ({
  entryDataResponse,
}) => {
  const defaultValues = {
    id: "",
    created: "",
    date_modified: "",
    appointment_date: "",
    entry_type: "",
    physician_owner: "",
    patient_owner: "",

    celery_task_id: "",
    celery_task_status: "",
    completed_celery_task_id: "",
    completed_celery_task_success: false,

    // Original images
    original_image_front: "",
    image_one_0_details: "{}",
    original_image_back: "",
    image_one_1_details: "{}",

    // Calculated overlays
    overlay_front: "",
    image_two_0_details: "{}",
    overlay_back: "",
    image_two_1_details: "{}",

    pair_configuration_0: "{}",
    pair_configuration_1: "{}",

    thumbnail: "",
    summary: "",
    notes: "",
    classification_tags: [],
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: entryDataResponse,
    defaultValues: defaultValues,
  });

  // Fields which require further sanitization: sanitizeImageDetails, sanitizePairConfiguration,
  // and sanitizeTags functions on the relevant fields.
  sanitizedResponse.image_one_0_details = sanitizeImageDetails(
    sanitizedResponse.image_one_0_details
  );
  sanitizedResponse.image_two_0_details = sanitizeImageDetails(
    sanitizedResponse.image_two_0_details
  );
  sanitizedResponse.image_one_1_details = sanitizeImageDetails(
    sanitizedResponse.image_one_1_details
  );
  sanitizedResponse.image_two_1_details = sanitizeImageDetails(
    sanitizedResponse.image_two_1_details
  );

  sanitizedResponse.pair_configuration_0 = sanitizePairConfiguration(
    sanitizedResponse.pair_configuration_0
  );
  sanitizedResponse.pair_configuration_1 = sanitizePairConfiguration(
    sanitizedResponse.pair_configuration_1
  );

  sanitizedResponse.classification_tags = sanitizeTags(
    sanitizedResponse.classification_tags
  );

  return sanitizedResponse;
};

/**
 * Processes the segmentation calculation form data and populates the FormData object with
 * the necessary images and their details.
 *
 * @param {Object} param0 - An object.
 * @param {FormData} param0.formData - The FormData object to be populated.
 * @param {Array} param0.comparisonItems - An array containing comparison items with images and their details.
 *
 * @returns {boolean} Returns true if the processing is successful, otherwise returns false with a warning message.
 *
 * @example
 *
 * processSegmentationCalculationFormData({ formData: formDataObj, comparisonItems: items });
 */
export const processSegmentationCalculationFormData = ({
  formData,
  comparisonItems,
}) => {
  const { fileOne: fileOneFront, fileOneDetails: fileOneDetailsFront } =
    comparisonItems[0];
  const { fileOne: fileOneBack, fileOneDetails: fileOneDetailsBack } =
    comparisonItems[1];

  // If there are no images in any items, display a warning and abort the processing
  if (fileOneFront.length === 0 && fileOneBack.length === 0) {
    toastManager(
      "warning",
      "Calculation Warning",
      "Cannot calculate entry with no input images. Add images to begin the calculation."
    );
    // Return false to indicate an issue during processing
    return false;
  }

  // Populate FormData with the first image and its details
  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOneFront,
    fileDetails: fileOneDetailsFront,
    fileName: "original_image_front",
    detailsName: "image_one_0_details",
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOneBack,
    fileDetails: fileOneDetailsBack,
    fileName: "original_image_back",
    detailsName: "image_one_1_details",
  });

  // Return true to indicate successful processing
  return true;
};

/**
 * Processes the segmentation save form data and populates the FormData object with
 * the necessary images' details and pair configurations.
 *
 * @param {Object} param0 - An object.
 * @param {FormData} param0.formData - The FormData object to be populated.
 * @param {Array} param0.comparisonItems - An array containing comparison items with images and their details.
 *
 * @example
 *
 * processSegmentationSaveFormData({ formData: formDataObj, comparisonItems: items });
 */
export const processSegmentationSaveFormData = ({
  formData,
  comparisonItems,
}) => {
  // Retrieve the valid configuration for the first comparison item
  const validPairConfigurationFront = getValidPairConfiguration({
    comparisonItem: comparisonItems[0],
  });

  const validPairConfigurationBack = getValidPairConfiguration({
    comparisonItem: comparisonItems[1],
  });

  const {
    fileOne: fileOneFront,
    fileTwo: fileTwoFront,
    fileOneDetails: fileOneDetailsFront,
    fileTwoDetails: fileTwoDetailsFront,
  } = comparisonItems[0];

  const {
    fileOne: fileOneBack,
    fileTwo: fileTwoBack,
    fileOneDetails: fileOneDetailsBack,
    fileTwoDetails: fileTwoDetailsBack,
  } = comparisonItems[1];

  // Append the valid configuration to FormData
  if (fileOneFront.length !== 0 && fileTwoFront.length !== 0) {
    formData.append(
      `pair_configuration_${0}`,
      JSON.stringify(validPairConfigurationFront)
    );
  }
  if (fileOneBack.length !== 0 && fileTwoBack.length !== 0) {
    formData.append(
      `pair_configuration_${1}`,
      JSON.stringify(validPairConfigurationBack)
    );
  }

  // Append the details of the  images to FormData without including the images
  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOneFront,
    fileDetails: fileOneDetailsFront,
    fileName: "original_image_front",
    detailsName: "image_one_0_details",
    appendImage: false,
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileTwoFront,
    fileDetails: fileTwoDetailsFront,
    fileName: "overlay_front",
    detailsName: "image_two_0_details",
    appendImage: false,
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOneBack,
    fileDetails: fileOneDetailsBack,
    fileName: "original_image_back",
    detailsName: "image_one_1_details",
    appendImage: false,
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileTwoBack,
    fileDetails: fileTwoDetailsBack,
    fileName: "overlay_back",
    detailsName: "image_two_1_details",
    appendImage: false,
  });
};

/**
 * Sanitizes the segmentation calculation result response, ensuring that
 * the overlay_front and overlay_back properties exist and have default values.
 *
 * @param {Object} param0 - An object.
 * @param {Object} param0.segmentationCalculationResultResponse - The response object from a segmentation calculation.
 * @returns {Object} An object with sanitized overlay_front and overlay_back properties.
 *
 * @example
 *
 * sanitizeSegmentationCalculationResultResponse({ segmentationCalculationResultResponse: responseObj });
 */
export const sanitizeSegmentationCalculationResultResponse = ({
  segmentationCalculationResultResponse = {},
}) => {
  const { overlay_front, overlay_back } = segmentationCalculationResultResponse;

  return {
    // Overlays
    overlay_front: overlay_front !== undefined ? overlay_front : "",
    overlay_back: overlay_back !== undefined ? overlay_back : "",
  };
};
